import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"

const MessageActionsContext = createContext(null)
const disabledMessageActions = {
    openMenu: () => { },
    closeMenu: () => { },
}

const getPopoverMetrics = (mode, viewportWidth, viewportHeight) => {
    const margin = 8
    if (mode === "reactions") return { width: Math.min(274, viewportWidth - margin * 2), height: 44, scroll: false }
    if (mode === "emojis") return { width: Math.min(470, viewportWidth - margin * 2), height: Math.min(450, viewportHeight - margin * 2), scroll: false }
    return { width: Math.min(260, viewportWidth - margin * 2), height: null, scroll: true }
}

const MessageActionsProvider = ({ children }) => {
    const [popover, setPopover] = useState(null)
    const menuRef = useRef(null)
    const closeCallbackRef = useRef(null)

    const closeMenu = useCallback(({ restoreFocus = false } = {}) => {
        const current = closeCallbackRef.current
        closeCallbackRef.current = null
        setPopover(previous => {
            if (restoreFocus) previous?.anchor?.focus?.()
            return null
        })
        current?.()
    }, [])

    const openMenu = useCallback((config) => {
        closeCallbackRef.current?.()
        closeCallbackRef.current = config.onClose || null

        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const margin = 8
        const metrics = getPopoverMetrics(config.mode, viewportWidth, viewportHeight)
        const estimatedHeight = config.estimatedHeight
            ? Math.min(config.estimatedHeight, viewportHeight - margin * 2)
            : metrics.height || Math.min(360, viewportHeight - margin * 2)
        const anchorRect = config.anchor?.getBoundingClientRect?.()
        const pointerEvent = config.event?.type === "contextmenu" ? config.event : null
        const anchorLeft = pointerEvent?.clientX ?? anchorRect?.left ?? margin
        const anchorRight = pointerEvent?.clientX ?? anchorRect?.right ?? margin
        const anchorTop = pointerEvent?.clientY ?? anchorRect?.top ?? margin
        const anchorBottom = pointerEvent?.clientY ?? anchorRect?.bottom ?? margin
        const preferredLeft = config.mode === "reactions" && config.direction === "send"
            ? anchorLeft
            : anchorRight - metrics.width
        const left = Math.min(Math.max(preferredLeft, margin), viewportWidth - metrics.width - margin)
        const top = anchorBottom + estimatedHeight + margin <= viewportHeight
            ? Math.max(margin, anchorBottom + 6)
            : Math.max(margin, anchorTop - estimatedHeight - 6)

        setPopover({
            ...config,
            anchor: config.anchor,
            style: {
                top: `${top}px`,
                left: `${left}px`,
                width: `${metrics.width}px`,
                maxWidth: `calc(100vw - ${margin * 2}px)`,
                ...(metrics.scroll ? {
                    maxHeight: `${viewportHeight - margin * 2}px`,
                    overflowY: "auto",
                } : {
                    overflow: "visible",
                }),
            },
        })
    }, [])

    useEffect(() => {
        if (!popover) return
        const menu = menuRef.current
        const items = [...(menu?.querySelectorAll('[role="menuitem"]') || [])]
        items[0]?.focus()

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                event.preventDefault()
                closeMenu({ restoreFocus: true })
                return
            }
            if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key) || !items.length) return
            event.preventDefault()
            const currentIndex = items.indexOf(document.activeElement)
            const nextIndex = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1
                : event.key === "ArrowDown" ? (currentIndex + 1 + items.length) % items.length
                    : (currentIndex - 1 + items.length) % items.length
            items[nextIndex]?.focus()
        }
        const closeDetachedMenu = () => closeMenu()
        menu?.addEventListener("keydown", handleKeyDown)
        window.addEventListener("resize", closeDetachedMenu)

        return () => {
            menu?.removeEventListener("keydown", handleKeyDown)
            window.removeEventListener("resize", closeDetachedMenu)
        }
    }, [popover, closeMenu])

    useEffect(() => () => {
        closeCallbackRef.current?.()
        closeCallbackRef.current = null
    }, [])

    const value = useMemo(() => ({ openMenu, closeMenu }), [openMenu, closeMenu])

    return (
        <MessageActionsContext.Provider value={value}>
            {children}
            {popover && createPortal(
                <>
                    <ul
                        ref={menuRef}
                        className={`${popover.menuClassName} ${popover.mode === "reactions" ? popover.compactClassName : ""}`}
                        role="menu"
                        aria-label={popover.ariaLabel}
                        style={popover.style}
                        onClick={() => closeMenu()}
                    >
                        {popover.content}
                    </ul>
                    <div className={popover.backgroundClassName} onClick={() => closeMenu()} />
                </>,
                document.body
            )}
        </MessageActionsContext.Provider>
    )
}

const useMessageActions = ({ optional = false } = {}) => {
    const context = useContext(MessageActionsContext)
    if (!context && optional) return disabledMessageActions
    if (!context) throw new Error("useMessageActions must be used inside MessageActionsProvider")
    return context
}

export { MessageActionsProvider, useMessageActions }
